import admiralQuestionsContainingPii, {
  admiralQuestionPiiQuestionAnsweredExclusionList,
} from '../Data/Maps/admiralQuestionsContainingPII';
import ValidationStatus from '../Data/ValidationStatus';
import { convertZeroToOneIndexIfNumber, deeplyEquals } from '../Utils/utils';
import logToConsole from '../Utils/logToConsole';
import {
  componentContainsCheckboxOptions,
  getOptionTier,
  getRadioDisplayValueFromComponent,
  getValueFromComponent,
} from './components';
import {
  isConditionallyActive,
  isHiddenAsPartOfMoveHubSupplierMatchLogic,
  isHiddenDueToSupplierMatchSelectionDisabled,
  isIntentionallyHidden,
} from './conditionality';
import { handleFormError } from './handleFormError';
import { GenericAdmiralFields } from '../Data/Components';
import {
  getLegalStatementToShowOnResultsPage,
  getStateAwareLegalStatement,
} from '../Components/LegalStatement/legalStatement.helpers';

// TODO: Write unit tests covering all cases
export const exposeEventToPartner = ({
  eventType,
  eventObject = {},
  recursiveCall = false,
  errorObject = {},
}) => {
  if (eventType === 'formError') {
    handleFormError({
      formId: String(eventObject.formId),
      errorMessage: errorObject.message,
      metricMessage: errorObject.metricMessage,
      iFrameId: eventObject.iFrameId,
      componentId: eventObject.componentId,
    });
  }
  /*
   All events that get sent up to the parent site of the widget need to contain an iFrameId.
   However, that iFrameId is not available instantly on widget load. It takes time to be passed in.
   To prevent the race condition where some initial events are fired without it being present,
   command buffering gets employed. Until the iFrameId becomes available, we'll buffer the events
   that come in on every function call of 'exposeEventToPartner' to bubble up later.
  */
  if (typeof window !== 'undefined') {
    if (!eventObject.iFrameId && !window?.__private__?.commandBuffer) {
      // Case 1 - To begin with no iFrameId is present and no command buffer is created yet.
      //          This logic creates the command buffer and stores this first incoming event.
      if (
        !window.__private__ ||
        typeof window.__private__ !== 'object' ||
        Array.isArray(window.__private__)
      ) {
        window.__private__ = {};
      }

      window.__private__.commandBuffer = [
        {
          function: exposeEventToPartner,
          arguments: {
            eventType,
            eventObject,
            errorObject,
          },
        },
      ];
    } else if (
      !eventObject.iFrameId &&
      window?.__private__?.commandBuffer &&
      Array.isArray(window.__private__.commandBuffer)
    ) {
      // Case 2 - There still is no iFrameId present but there now is a command buffer containing
      //          at least one event. This logic stores this next incoming subsequent event.

      // FIXME: This workaround prevents duplicate events (whilst render inefficiencies are in place across Chameleon)
      const lastCommandInQueue =
        window.__private__.commandBuffer[
          window.__private__.commandBuffer.length - 1
        ];
      // An argument comparison suffices, since all commands
      // in this buffer queue call the same function
      const lastCommandInQueueIsIdenticalToThisOne =
        lastCommandInQueue &&
        deeplyEquals(lastCommandInQueue.arguments, { eventType, eventObject });

      if (lastCommandInQueueIsIdenticalToThisOne) {
        return undefined;
      }

      window.__private__.commandBuffer.push({
        function: exposeEventToPartner,
        arguments: {
          eventType,
          eventObject,
          errorObject,
        },
      });
    } else if (
      eventObject.iFrameId &&
      window?.__private__?.commandBuffer &&
      Array.isArray(window.__private__.commandBuffer) &&
      window.__private__.commandBuffer.length > 0
    ) {
      // Case 3 - The iFrameId is now present and all buffered commands are still to be executed.
      //          This block will send the events up one by one (inserting the new iFrameId before doing so)
      //          Incoming commands will no longer need buffering after this.

      // Before running the queued commands, we need to add the incoming command to the end of the existing queue
      // The if statement prevents that once we've started clearing command after command from the queue that they get added straight back on
      if (!recursiveCall) {
        window.__private__.commandBuffer.push({
          function: exposeEventToPartner,
          arguments: {
            eventType,
            eventObject,
            errorObject,
          },
        });
      } else {
        // The buffer has started emptying but still has items in it
        // We have an iframeId now so we can send the message to the window
        const message = `${eventType}:${JSON.stringify(eventObject)}`;
        // Post message to the immediate parent of the current iframe window as this should be where the formLoader exists.
        if (window.parent) {
          window.parent.postMessage(message, '*');
        }
      }

      while (window.__private__.commandBuffer.length > 0) {
        /**
         * Since all commands inside the commandBuffer recursively call this function, we need to shrink the buffer on every call to prevent an infinite loop that uses up all stack space
         */
        const commandAndArgs = window.__private__.commandBuffer.shift();
        if (
          commandAndArgs.function &&
          commandAndArgs.arguments &&
          commandAndArgs.arguments.eventType &&
          commandAndArgs.arguments.eventObject
        ) {
          commandAndArgs.arguments.recursiveCall = true;
          /**
           * recursively call each function in the buffer queue until the buffer is empty
           *
           * This is done recursively to prevent the buffer from growing too large and causing a stack overflow. The arguments is an object that may consist of an eventObject or an errorObject as well a the eventType, but it is open to any other arguments that are passed in for the specific function being called.
           */
          commandAndArgs.function({
            eventType: commandAndArgs.arguments.eventType,
            eventObject: {
              ...commandAndArgs.arguments.eventObject,
              ...{ iFrameId: eventObject.iFrameId }, // i.e. eventObject.iFrameId is now present
            },
            recursiveCall: true,
          });
        }
      }
    } else if (
      eventObject.iFrameId &&
      (window?.__private__?.commandBuffer?.length === 0 ||
        !window?.__private__?.commandBuffer ||
        !Array.isArray(window.__private__?.commandBuffer))
    ) {
      // Case 4 - Lastly, the iFrameId is present and all buffered commands were executed or there never were any
      //          commands on hold. New incoming events can now instantly be bubbled up via this logic
      const message = `${eventType}:${JSON.stringify(eventObject)}`;
      // Post message to the immediate parent of the current iframe window as this should be where the formLoader exists.
      if (window.parent) {
        window.parent.postMessage(message, '*');
      }
    }
  }
  return undefined;
};

export const exposeFormErrorEvent = ({
  component,
  iFrameId,
  errorObject,
  formId,
}) => {
  const getFormErrorType = () => {
    const errorType = Object.keys(component.validation.constraints).filter(
      (constraint) =>
        component.validation.constraints[constraint].status ===
        ValidationStatus.INVALID
    );
    return errorType[0];
  };

  const eventObject = {
    // Convert zero based numbering to partner readable one based numbering.
    pageNumber: component.component.page + 1,
    componentInputType: component.input.type,
    componentId: component.component.id,
    iFrameId,
    formId,
    errorType: getFormErrorType(),
    currentQuestion: component.component.text,
  };

  exposeEventToPartner({
    eventType: 'formError',
    eventObject,
    errorObject,
  });
};

const shouldObfuscateValue = ({ isDomainInternal, mappedQuestionId }) => {
  const isInternalDomainAndOnPIIExclusionList =
    isDomainInternal &&
    admiralQuestionPiiQuestionAnsweredExclusionList.includes(mappedQuestionId);

  if (
    !isInternalDomainAndOnPIIExclusionList &&
    admiralQuestionsContainingPii.includes(mappedQuestionId)
  ) {
    return true;
  }

  return false;
};

const getQuestionAnsweredEventValueFromComponent = ({ component }) => {
  const value = getValueFromComponent(component);

  if (componentContainsCheckboxOptions(component)) {
    return value.join(', ');
  }

  return value;
};

export const exposeQuestionAnsweredEvent = ({
  component,
  iFrameId,
  isDomainInternal,
}) => {
  const shouldValueBeObfuscated = shouldObfuscateValue({
    isDomainInternal,
    mappedQuestionId: component.component.mappedQuestionId,
  });

  const isPII = admiralQuestionsContainingPii.includes(
    component.component.mappedQuestionId
  );

  const valueVisibility = isPII ? 'hidden' : 'visible';

  const isRadio = component.component.type === 'radios';

  const eventObject = {
    name: component.component.text,
    inputType: component.input.type,
    componentId: component.component.id,
    pageNumber: component.component.page + 1, // Convert zero based numbering to partner readable one based numbering.
    iFrameId,
    value: shouldValueBeObfuscated
      ? '********'
      : getQuestionAnsweredEventValueFromComponent({
          component,
        }),
    ...(isRadio && {
      valueLabel: getRadioDisplayValueFromComponent(component),
    }),
    valueVisibility, // If on the exclusion list, obfuscate the value in the translation layer rather than here
    visitorTier: getOptionTier(component) || '',
  };
  exposeEventToPartner({
    eventType: 'questionAnswered',
    eventObject,
    errorObject: {},
  });
};

export const exposeThankYouPageReachedEvent = ({
  iFrameId,
  submissionId,
  showCustomerMatchesOnThankYouPage,
}) => {
  exposeEventToPartner({
    eventType: 'thankYouPageReached',
    eventObject: {
      iFrameId,
      submissionId,
      showCustomerMatchesOnThankYouPage,
    },
  });
};

export const requestIdentityUpdateEvent = (iFrameId) => {
  exposeEventToPartner({
    eventType: 'userIdentityUpdateRequested',
    eventObject: { iFrameId },
  });
};

const isVisibleToCustomer = (
  components,
  component,
  subcategoryId,
  phoneNumberCountryCode
) => {
  return (
    isConditionallyActive(components, component) &&
    !isIntentionallyHidden(component) &&
    component.component?.isVisible !== false &&
    !isHiddenAsPartOfMoveHubSupplierMatchLogic({
      component,
      components,
      subcategoryId,
      phoneNumberCountryCode,
    }) &&
    !isHiddenDueToSupplierMatchSelectionDisabled(component)
  );
};

export const formatComponentData = ({
  currentPage,
  components,
  subcategoryId,
  phoneNumberCountryCode,
}) => {
  const formComponents = {};
  const pageComponents = [];
  Object.values(components).forEach((component) => {
    const componentId = component.component.id;
    if (componentId) {
      formComponents[componentId] = {
        name: component?.component?.text,
        inputType: component?.component?.type,
        page: component.component.page + 1, // Convert zero indexed pages.
        isVisible: isVisibleToCustomer(
          components,
          component,
          subcategoryId,
          phoneNumberCountryCode
        ),
      };

      // Send all components on the page including hidden components.
      if (component.component.page === currentPage) {
        pageComponents.push(componentId);
      }
    }
  });

  // Convert zero based numbering to partner readable one based numbering.
  return {
    pageComponents,
    formComponents,
  };
};

export const exposeUserActionEvent = ({ type, iFrameId, actionDetails = {} }) =>
  exposeEventToPartner({
    eventType: 'userAction',
    eventObject: {
      type,
      iFrameId,
      actionDetails,
    },
  });

export const exposePageChangedEvent = ({
  fromPage,
  destinationPage,
  components,
  iFrameId,
  submissionPage,
  subcategoryId,
  phoneNumberCountryCode,
  emailConsentStatementShown,
  isFinalPageBeforeSubmission,
  matchConfidenceLevelShown,
  formId,
  formOriginId,
}) =>
  exposeEventToPartner({
    eventType: 'pageChanged',
    eventObject: {
      fromPage: convertZeroToOneIndexIfNumber(fromPage),
      destinationPage: convertZeroToOneIndexIfNumber(destinationPage),
      submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
      emailConsentStatementShown,
      iFrameId,
      isFinalPageBeforeSubmission,
      matchConfidenceLevelShown,
      formId,
      formOriginId,
      ...formatComponentData({
        currentPage: destinationPage,
        components,
        subcategoryId,
        phoneNumberCountryCode,
      }),
    },
  });

/**
 * Extract form metadata (component IDs and types) for results page
 * This metadata is used by the results page to understand the form structure
 */
export const extractFormMetadata = (components) => {
  const metadata = {};

  Object.entries(components).forEach(([componentId, component]) => {
    if (component?.component) {
      metadata[componentId] = {
        id: component.component.id,
        type: component.input?.type || component.component?.type,
        mappedQuestionId: component.component.mappedQuestionId,
        text: component.component?.text,
        page: component.component.page,
      };
    }
  });

  return metadata;
};

export const exposeInitialWidgetLoadEvent = ({
  currentPage,
  components,
  iFrameId,
  widgetLabel,
  subcategoryName,
  subcategoryId,
  categoryName,
  categoryId,
  sectorName,
  tradingHubId,
  tradingHubName,
  visitorId,
  submissionPage,
  phoneNumberCountryCode,
  componentExperiments,
  subcategoryMaxMatches,
  formId,
  formOriginId,
  isPolarisForm,
}) => {
  const formMetadata = extractFormMetadata(components);

  exposeEventToPartner({
    eventType: 'initialWidgetLoad',
    eventObject: {
      currentPage: convertZeroToOneIndexIfNumber(currentPage),
      iFrameId,
      widgetLabel,
      subcategoryName,
      subcategoryId,
      categoryName,
      categoryId,
      sectorName,
      tradingHubId,
      tradingHubName,
      visitorId,
      submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
      componentExperiments,
      formMetadata,
      ...(isPolarisForm ? { isPolarisForm: true } : {}),
      subcategoryMaxMatches,
      formId,
      formOriginId,
      ...formatComponentData({
        currentPage,
        components,
        subcategoryId,
        phoneNumberCountryCode,
      }),
    },
  });
};

/**
 * Collects the unique, non-empty brand ids across the submitted and shown
 * matches so the event can expose a flat, deduplicated brandIds array.
 */
export const getShownMatchBrandIds = ({ submitted = [], shown = [] }) => [
  ...new Set(
    [...submitted, ...shown].map((match) => match?.brandId).filter(Boolean)
  ),
];

export const exposeSelectAndConnectShown = ({
  iFrameId,
  formId,
  visitorId,
  matches,
}) => {
  if (!iFrameId || !formId || !visitorId || !matches) {
    logToConsole.warn(
      'exposeSelectAndConnectShown - missing required arguments - event not emitted. Expected iFrameId, formId, visitorId and matches.'
    );
    return;
  }

  exposeEventToPartner({
    eventType: 'selectAndConnectShown',
    eventObject: {
      iFrameId,
      formId,
      visitorId,
      matches,
      brandIds: getShownMatchBrandIds(matches),
    },
  });
};

export const exposeFailedForwardPageNavigationEvent = ({
  iFrameId,
  currentPage,
}) =>
  exposeEventToPartner({
    eventType: 'failedForwardPageNavigation',
    eventObject: {
      iFrameId,
      currentPage: currentPage + 1,
    },
  });

export const exposeNavigationControlledEvent = ({
  iFrameId,
  currentPage,
  direction,
  display,
}) =>
  exposeEventToPartner({
    eventType: 'navigationControlled',
    eventObject: {
      iFrameId,
      currentPage: currentPage + 1,
      direction,
      display,
    },
  });

const customerMatchLevelNameMap = {
  LIVE: 'live',
  SEVEN_DAY: '7day',
  PREDEFINED: 'predefined',
  SUBCATEGORY_REVENUE_FALLBACK: 'subcategoryFallback',
};

export const convertCustomerMatchLevelToEventValue = (name) => {
  if (Object.keys(customerMatchLevelNameMap).includes(name)) {
    return customerMatchLevelNameMap[name];
  }
};

export const exposeSupplierMatchesRequestedEvent = ({
  eventName,
  iFrameId,
  isSuccess,
  maxMatches,
  customerMatchLevel,
  isMatchImpliedByPrivacyStatement,
  supplierMatches = [],
}) => {
  const brandNamesString = supplierMatches.map((match) => {
    if (match.brandId && match.brandLabel) {
      return match.brandLabel;
    }

    return match.brandFilterValue;
  });

  const brandMatches = supplierMatches
    .filter((match) => match.brandId && match.brandLabel)
    .map((match) => ({
      id: match.brandId,
      name: match.brandLabel,
    }));

  const customerMatchLevelAsEventValue =
    convertCustomerMatchLevelToEventValue(customerMatchLevel);

  exposeEventToPartner({
    eventType: eventName,
    eventObject: {
      iFrameId,
      isSuccess,
      maxMatches,
      matches: brandNamesString,
      brandMatches,
      matchLevel: customerMatchLevelAsEventValue,
      isMatchImpliedByPrivacyStatement,
    },
  });
};

/**
 * Relays the gateway's score summary (the full AI cutoff decision) to the hosting page.
 * Forwarded verbatim — the caller gates on scoreSummary being non-null before calling.
 */
export const exposeScoreSummaryEvent = ({
  iFrameId,
  visitorId,
  scoreSummary,
}) => {
  exposeEventToPartner({
    eventType: 'scoreSummaryReceived',
    eventObject: {
      iFrameId,
      visitorId,
      scoreSummary,
    },
  });
};

export const exposeMatchPoolUpdatedEvent = ({ source, count, iFrameId }) => {
  if (!Number.isFinite(count) || !source) {
    return;
  }

  exposeEventToPartner({
    eventType: 'matchPoolUpdated',
    eventObject: {
      event: 'matchPoolUpdated',
      source,
      count,
      iFrameId,
    },
  });
};

// Every source (supplier match component options, customer matches, subcategory
// fallbacks and dynamic matches) reaches the event as a component option, the
// shape convertMatchToOption produces.
const toBrandMatchEventEntry = (option) => {
  if (!option?.id || !option.value) return undefined;

  return {
    id: option.id,
    name: option.value,
    supplierId: option.supplierId ?? null,
    brandRevisionId: option.brandRevisionId ?? null,
    matchLevel: option.matchLevel ?? null,
    logo: option.icon_url ?? null,
    usps: option.usps ?? [],
  };
};

const toValidBrandMatchEventEntries = (matches) =>
  (Array.isArray(matches) ? matches : [])
    .map(toBrandMatchEventEntry)
    .filter((entry) => entry !== undefined);

/**
 * Builds the brandMatches array for the thankYouPageRequested event: one entry
 * per brand shown to the customer, in display order (1-based index), with
 * status "submitted" when the customer left it selected and "shown" otherwise.
 * Each entry carries the registry fields (supplier, revision, match level, logo
 * and USPs); a brand with no registry USPs keeps an empty list. Missing fields
 * are null rather than absent. Submitted brands absent from the shown list are
 * still reported, after the shown ones.
 */
export const toCustomerBrandMatchesEventBody = ({
  shownBrandMatches,
  submittedBrandMatches,
}) => {
  const submitted = toValidBrandMatchEventEntries(submittedBrandMatches);
  const shown = toValidBrandMatchEventEntries(shownBrandMatches);
  const shownIds = new Set(shown.map((entry) => String(entry.id)));
  const submittedIds = new Set(submitted.map((entry) => String(entry.id)));

  const submittedNotShown = submitted.filter(
    (entry) => !shownIds.has(String(entry.id))
  );

  return [...shown, ...submittedNotShown].map((entry, position) => ({
    ...entry,
    index: position + 1,
    status: submittedIds.has(String(entry.id)) ? 'submitted' : 'shown',
  }));
};

export const exposeBrandMatchReasonsEvent = ({
  iFrameId,
  brandMatchReasons,
}) => {
  exposeEventToPartner({
    eventType: 'brandMatchReasonsReceived',
    eventObject: {
      iFrameId,
      brandMatchReasons,
    },
  });
};

export const requestLyticsSegmentsEvent = ({ iFrameId }) => {
  exposeEventToPartner({
    eventType: 'lyticsSegmentsRequested',
    eventObject: { iFrameId },
  });
};

export const exposeMatchConfidenceLevelChangedEvent = ({
  confidenceLevel,
  previousLevel,
  iFrameId,
}) => {
  exposeEventToPartner({
    eventType: 'matchConfidenceLevelChanged',
    eventObject: {
      confidenceLevel,
      previousLevel,
      iFrameId,
    },
  });
};

export const exposeCustomerMatchResultsRequestedEvent = ({
  iFrameId,
  matchedBrands,
  components = {},
  legalStatement,
  customerStateCode,
  subcategoryName,
  subcategoryId,
  supplierId,
  customerMatches,
  subcategoryMaxMatches,
  captureBsaLeadsOnAbandonedFlows = false,
}) => {
  const brandMatches = matchedBrands.map((match) => ({
    id: match.brandId,
    name: match.brandLabel,
    logo: match.brandLogoUrl,
  }));

  // Extract conversation answers from components
  const conversationAnswers = {};
  Object.entries(components).forEach(([componentId, component]) => {
    const value = component?.input?.value;
    if (value !== undefined && value !== null && value !== '') {
      // Use mappedQuestionId if available (e.g., 'q1', 'q2'), otherwise use componentId
      const questionId = component?.component?.mappedQuestionId || componentId;
      const questionLabel = component?.component?.text;
      const questionIdIsGenericAdmiralField =
        Object.values(GenericAdmiralFields).includes(questionId);
      // Handle array values (multi-select) - join them
      const finalValue = Array.isArray(value) ? value.join(', ') : value;
      const conversationAnswerKey =
        (questionIdIsGenericAdmiralField ? questionId : questionLabel) ||
        questionId;
      conversationAnswers[conversationAnswerKey] = finalValue;
    }
  });
  const stateAwareLegalStatement = getStateAwareLegalStatement({
    legalStatement: legalStatement ?? {},
    customerStateCode,
  });
  const finalLegalStatementText = getLegalStatementToShowOnResultsPage({
    supplierId,
    singleSupplierStatement:
      stateAwareLegalStatement.supplierLegalStatements?.singleSupplierStatement,
    customerMatches,
    statement: stateAwareLegalStatement.statement,
    statementOverride: legalStatement?.statementOverride,
  });
  const suppliersModal = legalStatement?.suppliersModal;
  exposeEventToPartner({
    eventType: 'customerMatchResultsRequested',
    eventObject: {
      iFrameId,
      brandMatches,
      conversationAnswers,
      legalConsentStatement: finalLegalStatementText,
      subcategoryName,
      subcategoryId,
      suppliersModal,
      subcategoryMaxMatches,
      showLegalStatement: !captureBsaLeadsOnAbandonedFlows,
    },
  });
};
